import { useState } from "react";
import { Network, User, Bot, Layers, Copy, Check, TicketPlus, AlertCircle, RotateCcw, ListTree } from "lucide-react";
import AnswerSources from "../elements/answer-sources";
import { ChatExchange } from "../pages/settings-panels/chat-usage-view";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import { cn, parseServerUtcMs, stripBreezeAiFences } from "@/lib/utils";
import { Message } from "@/types/chat";
import { ContentMessage, DocMessage } from "../elements/message-varients";
import { UnifiedModelIcon } from "@/assets/svg-icons/unified-model-icon";
import { AGENTS, TOAST_DURATION } from "@/lib/constants";
import MarkdownRenderer from "../elements/markdown-renderer";
import PersonaWorkflowRenderer from "../elements/persona-workflow-renderer";
import CitationRenderer from "../elements/citation-renderer";
import TabbedMessageRenderer, { parseTabbedContent } from "../elements/tabbed-message-renderer";

const NO_ANSWER_TEXT =
  "I couldn't find an answer to that. Try rephrasing the question or making it more specific.";
const FAILED_TEXT =
  "Sorry, something went wrong while processing your request. Please try again.";
const CANCELLED_TEXT = "Response was interrupted.";

// Always shows date + time on every message so a reader can see when each
// request and response happened without scrolling to a divider:
//   this year   → "5 Aug, 3:31 PM"
//   prior years → "5 Aug 2025, 3:31 PM"
function formatMessageTimestamp(value?: string): string {
  const ms = parseServerUtcMs(value);
  if (!Number.isFinite(ms)) return "";
  const d = new Date(ms);

  const sameYear = d.getFullYear() === new Date().getFullYear();
  const datePart = d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
  });
  const time = d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${datePart}, ${time}`;
}

export default function MessageLayout({
  index,
  message,
  handleOnClick,
  handleGenerateMatrix,
  handleGenerateDiagram,
  isMatrixButtonDisabled = false,
  isDiagramButtonDisabled = false,
  canRaiseJira = false,
  onRaiseJiraTicket,
  onRetry,
  canViewRunDetails = false,
}: {
  index: number;
  message: Message;
  handleOnClick?: (message: Message) => void;
  handleGenerateMatrix?: (message: Message, index: number) => void;
  handleGenerateDiagram?: (message: Message, index: number) => void;
  isMatrixButtonDisabled?: boolean;
  isDiagramButtonDisabled?: boolean;
  // False when the project has no linked Jira board — the button is hidden
  // rather than disabled, since there is no action the user could take to
  // resolve it from here.
  canRaiseJira?: boolean;
  // Delegated upward: this component stays presentational, and the conversation
  // slice needed to draft a ticket only exists in the parent.
  onRaiseJiraTicket?: (message: Message, index: number) => void;
  // Offered on a reply that ended without an answer (failed / no_answer / cancelled).
  onRetry?: (index: number) => void;
  // Platform admins and the project's owners / editors may open how an answer was produced (run history's view).
  canViewRunDetails?: boolean;
}) {
  const [disableFunctionalMetrics, setDisableFunctionalMetrics] =
    useState(false);
  const [disableDiagramGeneration, setDisableDiagramGeneration] =
    useState(false);
  const [copied, setCopied] = useState(false);
  const [runOpen, setRunOpen] = useState(false);

  const { isUser, text, agent } = message;
  const timestampLabel = formatMessageTimestamp(message.createdAt);
  const {
    isGherkinScriptQuery,
    gitUrl,
    isGitQuery,
    fileType,
    fileName,
    fileUrl,
    outputFileType,
    uploadedType,
  } = message.metaData ?? {};

  const isOutputHtml = outputFileType === "html";
  const isDocumentGeneration = !!outputFileType;
  const shouldHideGitButtons =
    isGitQuery && text === "No README found for this branch.";

  const showDocMessage = fileType || gitUrl;
  const showGenerateMetricsButton = !shouldHideGitButtons && uploadedType && agent !== AGENTS.ARCHITECTURE_AGENT;
  const showJsonViewButton =
    !shouldHideGitButtons &&
    (isGherkinScriptQuery ||
      (isGitQuery && text !== "git url is not correct") ||
      isDocumentGeneration);

  const handleMetricsClick = () => {
    if (!handleGenerateMatrix) return;
    handleGenerateMatrix(message, index);
    setDisableFunctionalMetrics(true);
  };

  const handleDiagramClick = () => {
    if (!handleGenerateDiagram) return;
    handleGenerateDiagram(message, index);
    setDisableDiagramGeneration(true);
  };

  const getCopyableText = () => stripBreezeAiFences(text);

  const handleCopy = async () => {
    const content = getCopyableText();
    if (!content) return;
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      toast.error("Failed to copy to clipboard", { duration: TOAST_DURATION });
    }
  };

  const isArchitectureAgent = agent === AGENTS.ARCHITECTURE_AGENT;
  const showDiagramButton = isArchitectureAgent && uploadedType;

  // Function to detect and extract breezeai content (JSON and Citations)
  const extractBreezeAIContent = (content: string) => {
    const matches = [];

    // Extract BreezeAI JSON content
    const breezeAIJsonRegex = /```breezeaijson\s*([\s\S]*?)\s*```/g;
    let match;

    while ((match = breezeAIJsonRegex.exec(content)) !== null) {
      try {
        const jsonData = JSON.parse(match[1].trim());
        matches.push({
          type: "json",
          fullMatch: match[0],
          data: jsonData,
          startIndex: match.index,
          endIndex: match.index + match[0].length,
        });
      } catch (error) {
        console.error("Failed to parse BreezeAI JSON:", error);
        console.error("Raw JSON content:", match[1]);
        // Add error state to matches so it can be rendered with fallback
        matches.push({
          type: "json-error",
          fullMatch: match[0],
          data: {
            originalContent: match[1].trim(),
            error: error instanceof Error ? error.message : String(error),
          },
          startIndex: match.index,
          endIndex: match.index + match[0].length,
        });
      }
    }

    // Extract BreezeAI Citations content
    const breezeAICitationsRegex = /```breezeaicitations\s*([\s\S]*?)\s*```/g;

    while ((match = breezeAICitationsRegex.exec(content)) !== null) {
      try {
        const citationData = JSON.parse(match[1].trim());
        if (
          Array.isArray(citationData) &&
          citationData.every((item) => typeof item === "string")
        ) {
          matches.push({
            type: "citation",
            fullMatch: match[0],
            data: citationData,
            startIndex: match.index,
            endIndex: match.index + match[0].length,
          });
        } else {
          throw new Error("Citations data must be an array of strings");
        }
      } catch (error) {
        console.error("Failed to parse BreezeAI Citations:", error);
        console.error("Raw Citations content:", match[1]);
        // Add error state to matches so it can be rendered with fallback
        matches.push({
          type: "citation-error",
          fullMatch: match[0],
          data: {
            originalContent: match[1].trim(),
            error: error instanceof Error ? error.message : String(error),
          },
          startIndex: match.index,
          endIndex: match.index + match[0].length,
        });
      }
    }

    // Sort matches by start index to process them in order
    return matches.sort((a, b) => a.startIndex - b.startIndex);
  };

  // A reply that ended without an answer gets its own error bubble instead of
  // an empty or generic assistant message.
  if (
    !isUser &&
    (message.processingStatus === "failed" ||
      message.processingStatus === "no_answer" ||
      message.processingStatus === "cancelled")
  ) {
    const isFailed = message.processingStatus === "failed";
    const bubbleText = isFailed
      ? text?.trim() || FAILED_TEXT
      : message.processingStatus === "cancelled"
        ? CANCELLED_TEXT
        : NO_ANSWER_TEXT;
    return (
      <div className="flex gap-3 w-full flex-row">
        <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-surface-hover text-text-primary">
          <Bot className="w-4 h-4" />
        </div>
        <div className="flex flex-col gap-2 min-w-0 items-start">
          <div
            role="alert"
            className={cn(
              "max-w-3xl py-3 px-4 rounded-2xl rounded-tl-sm text-base leading-relaxed border flex items-start gap-2",
              isFailed
                ? "bg-error/10 border-error/30 text-text-primary"
                : "bg-surface-card border-surface-border text-text-primary"
            )}
          >
            <AlertCircle
              className={cn("w-4 h-4 mt-1 flex-shrink-0", isFailed ? "text-error" : "text-muted")}
              aria-hidden="true"
            />
            <span>{bubbleText}</span>
          </div>
          <div className="flex items-center justify-between w-full -mt-1.5 px-1">
            {onRetry ? (
              <button
                type="button"
                onClick={() => onRetry(index)}
                title="Put your question back in the message box"
                className="flex items-center gap-1 px-2 py-0.5 text-xs text-muted hover:text-text-primary transition-colors rounded-md hover:bg-surface-hover"
              >
                <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" />
                Retry
              </button>
            ) : (
              <span />
            )}
            {timestampLabel && (
              <span className="text-[11px] text-muted">{timestampLabel}</span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Check if message contains breezeai content (JSON or Citations)
  const breezeAIMatches = text ? extractBreezeAIContent(text) : [];
  const hasBreezeAIContent = breezeAIMatches.length > 0;

  // Deep-analysis multi-graph responses are fenced with <<<<<TAB:KEY>>>>>
  // markers. If one or more are present, render the message as tabs.
  const tabbedSections = !isUser ? parseTabbedContent(text) : [];
  const hasTabbedContent = tabbedSections.length > 0;

  return (
    <div
      className={cn(
        "flex gap-2.5 w-full",
        isUser ? "flex-row-reverse" : "flex-row"
      )}
    >
      {/* Avatar */}
      <div
        className={cn(
          "flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center",
          isUser
            ? "bg-primary text-text-on-color"
            : "bg-surface-hover text-text-primary"
        )}
      >
        {isUser ? (
          <User className="w-4 h-4" />
        ) : (
          <Bot className="w-4 h-4" />
        )}
      </div>

      {/* Message Content */}
      <div
        className={cn(
          "flex flex-col gap-2 min-w-0",
          isUser ? "items-end" : "items-start"
        )}
      >
        {showDocMessage && (
          <DocMessage
            fileUrl={
              gitUrl ? gitUrl : fileName || fileUrl?.split("/").pop() || ""
            }
            fileType={gitUrl ? "git" : fileType || ""}
          />
        )}

        <div
          className={cn(
            "py-2 px-3 rounded-2xl break-words text-base leading-relaxed",
            isUser
              ? "max-w-md bg-primary text-button-text rounded-tr-sm"
              : "max-w-3xl bg-surface-card text-text-primary rounded-tl-sm border border-surface-border"
          )}
        >
        {hasTabbedContent ? (
          <TabbedMessageRenderer text={text || ""} />
        ) : hasBreezeAIContent ? (
          <div className="space-y-4">
            {/* Render content with BreezeAI sections replaced */}
            {(() => {
              let lastIndex = 0;
              const contentParts = [];

              breezeAIMatches.forEach((match, idx) => {
                // Add text before this match
                if (match.startIndex > lastIndex) {
                  const beforeText = text!
                    .substring(lastIndex, match.startIndex)
                    .trim();
                  if (beforeText) {
                    contentParts.push(
                      <div key={`text-${idx}`}>
                        <MarkdownRenderer content={beforeText} />
                      </div>
                    );
                  }
                }

                // Add the appropriate BreezeAI renderer based on type
                if (match.type === "json") {
                  contentParts.push(
                    <div key={`breezeai-json-${idx}`} className="my-4">
                      <PersonaWorkflowRenderer data={match.data} />
                    </div>
                  );
                } else if (match.type === "json-error") {
                  contentParts.push(
                    <div key={`breezeai-json-error-${idx}`} className="my-4">
                      <div className="bg-error/10 border border-error/30 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-2 h-2 bg-error rounded-full"></div>
                          <div className="text-error font-semibold">
                            BreezeAI JSON Parse Error
                          </div>
                        </div>
                        <div className="text-muted text-sm mb-3">
                          Failed to parse JSON content. The original content is
                          shown below:
                        </div>
                        <div className="bg-surface-dark border border-surface-border rounded p-3">
                          <div className="text-xs text-muted mb-2 font-medium">
                            Original Content:
                          </div>
                          <pre className="text-xs text-white whitespace-pre-wrap break-words">
                            {match.data.originalContent}
                          </pre>
                        </div>
                        <details className="mt-3">
                          <summary className="text-error cursor-pointer text-sm hover:text-error/80">
                            Show error details
                          </summary>
                          <pre className="mt-2 text-xs bg-surface-input p-2 rounded border text-muted overflow-x-auto">
                            {match.data.error}
                          </pre>
                        </details>
                      </div>
                    </div>
                  );
                } else if (match.type === "citation") {
                  contentParts.push(
                    <div key={`breezeai-citation-${idx}`} className="my-4">
                      <CitationRenderer citations={match.data} />
                    </div>
                  );
                } else if (match.type === "citation-error") {
                  contentParts.push(
                    <div
                      key={`breezeai-citation-error-${idx}`}
                      className="my-4"
                    >
                      <div className="bg-error/10 border border-error/30 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-2 h-2 bg-error rounded-full"></div>
                          <div className="text-error font-semibold">
                            BreezeAI Citations Parse Error
                          </div>
                        </div>
                        <div className="text-muted text-sm mb-3">
                          Failed to parse citations content. Expected an array
                          of strings. The original content is shown below:
                        </div>
                        <div className="bg-surface-dark border border-surface-border rounded p-3">
                          <div className="text-xs text-muted mb-2 font-medium">
                            Original Content:
                          </div>
                          <pre className="text-xs text-white whitespace-pre-wrap break-words">
                            {match.data.originalContent}
                          </pre>
                        </div>
                        <details className="mt-3">
                          <summary className="text-error cursor-pointer text-sm hover:text-error/80">
                            Show error details
                          </summary>
                          <pre className="mt-2 text-xs bg-surface-input p-2 rounded border text-muted overflow-x-auto">
                            {match.data.error}
                          </pre>
                        </details>
                      </div>
                    </div>
                  );
                }

                lastIndex = match.endIndex;
              });

              // Add remaining text after the last match
              if (lastIndex < text!.length) {
                const afterText = text!.substring(lastIndex).trim();
                if (afterText) {
                  contentParts.push(
                    <div key="text-final">
                      <MarkdownRenderer content={afterText} />
                    </div>
                  );
                }
              }

              return contentParts;
            })()}
          </div>
        ) : (
          <MarkdownRenderer
            content={
              isGherkinScriptQuery ? "Gherkin script is ready!" : text || ""
            }
          />
        )}
        </div>

        {!isUser && <AnswerSources sources={message.sources} />}

        {/* Caption row: Copy button (left) + timestamp (right) share a single
            row so the timestamp always right-aligns with the bubble edge and
            the Copy button sits inline instead of on a separate line. On user
            messages there's no Copy — the empty span keeps justify-between
            pushing the timestamp to the right. */}
        {(timestampLabel || (!isUser && getCopyableText())) && (
          <div className="flex items-center justify-between w-full -mt-1.5 px-1">
            {!isUser && getCopyableText() ? (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={handleCopy}
                  title={copied ? "Copied!" : "Copy to clipboard"}
                  aria-label={copied ? "Copied to clipboard" : "Copy to clipboard"}
                  className="flex items-center gap-1 px-2 py-0.5 text-xs text-muted hover:text-text-primary transition-colors rounded-md hover:bg-surface-hover"
                >
                  {copied ? (
                    <>
                      <Check className="w-3.5 h-3.5" aria-hidden="true" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" aria-hidden="true" />
                      Copy
                    </>
                  )}
                </button>

                {/* Shares the Copy button's class list so the two read as one
                    control group rather than two competing affordances. */}
                {canRaiseJira && onRaiseJiraTicket && (
                  <button
                    type="button"
                    onClick={() => onRaiseJiraTicket(message, index)}
                    title="Raise a Jira ticket from this conversation"
                    aria-label="Raise a Jira ticket from this conversation"
                    className="flex items-center gap-1 px-2 py-0.5 text-xs text-muted hover:text-text-primary transition-colors rounded-md hover:bg-surface-hover"
                  >
                    <TicketPlus className="w-3.5 h-3.5" aria-hidden="true" />
                    Raise Jira Ticket
                  </button>
                )}

                {canViewRunDetails && message._id != null && (
                  <button
                    type="button"
                    onClick={() => setRunOpen(true)}
                    title="How this answer was produced: tool calls, model calls, time and cost"
                    aria-label="Run details"
                    className="flex items-center gap-1 px-2 py-0.5 text-xs text-muted hover:text-text-primary transition-colors rounded-md hover:bg-surface-hover"
                  >
                    <ListTree className="w-3.5 h-3.5" aria-hidden="true" />
                    Run details
                  </button>
                )}
              </div>
            ) : (
              <span />
            )}
            {timestampLabel && (
              <span className="text-[11px] text-muted">{timestampLabel}</span>
            )}
          </div>
        )}

        {runOpen && message._id != null && (
          <Dialog open={runOpen} onOpenChange={setRunOpen}>
            <DialogContent className="sm:max-w-5xl max-h-[85vh] overflow-auto">
              <DialogTitle className="text-sm">Run details</DialogTitle>
              <ChatExchange id={message._id} />
            </DialogContent>
          </Dialog>
        )}

        {/* Action Buttons */}
        {(showGenerateMetricsButton || showJsonViewButton || showDiagramButton) && (
          <div className="flex flex-wrap gap-2 mt-1">
            {showGenerateMetricsButton && (
              <button
                disabled={isMatrixButtonDisabled || disableFunctionalMetrics}
                className="disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleMetricsClick}
              >
                <ContentMessage
                  icon={<Network className="h-4 w-4" />}
                  message={`Generate ${
                    agent === AGENTS.DESIGN_AGENT ? "Design" : "Functional"
                  } Metrics`}
                />
              </button>
            )}

            {showDiagramButton && (
              <button
                disabled={isDiagramButtonDisabled || disableDiagramGeneration}
                className="disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleDiagramClick}
              >
                <ContentMessage
                  icon={<Layers className="h-4 w-4" />}
                  message="Generate Diagram"
                />
              </button>
            )}

            {showJsonViewButton && (
              <button onClick={() => handleOnClick?.(message)}>
                <ContentMessage
                  icon={<UnifiedModelIcon />}
                  message={
                    isOutputHtml
                      ? "View Page"
                      : isDocumentGeneration
                      ? "View Document"
                      : isGherkinScriptQuery
                      ? "View Gherkin Script"
                      : "View Git Markdown"
                  }
                />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
